import React from "react";
import gql from "graphql-tag";
import { useQuery } from "react-apollo";
import { oc } from "ts-optchain";
import isEmpty from "lodash/isEmpty";
import { Text } from "@atoms";
import { useLoadingDelay, useStoreAppValue } from "@hooks";
import { LinkCategory } from "@types";
import { TFilterType, TActiveFilterType } from "../types";
import BrandListContent from "./BrandListContent";
import { BrandDirectoryQuery as BrandDirectoryQueryType } from "./__generated__/BrandDirectoryQuery";

// == Types ================================================================

interface IProps {
  activeFilter: TActiveFilterType;
  filterType: TFilterType;
  linkCategoryFilter: LinkCategory | null;
}

// == Constants ============================================================

const BRANDS_DIRECTORY_QUERY = gql`
  query BrandListContentQuery(
    $orderBy: BrandDirectoryOrderBy
    $firstLetter: String
    $currency: Currency
    $after: String
    $first: Int
    $linkCategory: LinkCategory
  ) {
    BrandDirectory(
      orderBy: $orderBy
      firstLetter: $firstLetter
      linkCategory: $linkCategory
      currency: $currency
      after: $after
      first: $first
    )
      @connection(
        key: "BrandDirectory"
        filter: ["currency", "firstLetter", "orderBy", "linkCategory"]
      ) {
      edges {
        cursor
        node {
          id
          ...BrandListFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${BrandListContent.fragments.fields}
`;

// == Component ============================================================

export const BrandListContentQuery = ({ activeFilter, filterType, linkCategoryFilter }: IProps) => {
  const { currency } = useStoreAppValue();
  const loadingDelayReady = useLoadingDelay();

  const queryVariables = () => {
    return {
      orderBy: filterType === "NAMED_SORT" ? `${activeFilter}_DESC` : "NAME_ASC",
      firstLetter: filterType === "LETTER" ? activeFilter : null,
      currency,
      after: null,
      first: 99,
      linkCategory: linkCategoryFilter,
    };
  };

  const { loading, data, error, fetchMore, networkStatus } = useQuery<BrandDirectoryQueryType>(
    BRANDS_DIRECTORY_QUERY,
    {
      variables: queryVariables(),
      notifyOnNetworkStatusChange: true,
    }
  );

  // nodeReducer
  const reduceBrands = (edgedBrands) => {
    const reducedBrands = oc(edgedBrands).BrandDirectory.edges([]);
    if (reducedBrands.length === 0) return [];

    const nodeReducer = (acc, brand) => {
      if (!brand.node) return acc;
      return [...acc, brand.node];
    };

    return reducedBrands.reduce(nodeReducer, []);
  };

  if (error) {
    return <Text>{error.message}</Text>;
  }

  // TODO: add loading for this component
  // if (!data || loading || !loadingDelayReady) return <LoadingBox />;
  if ((isEmpty(data) && loading) || !loadingDelayReady) return null;
  const brands = oc(data).BrandDirectory.edges();
  if (!brands) return null; // return error

  const onFetchMore = () => {
    const { endCursor } = oc(data).BrandDirectory.pageInfo();
    fetchMore({
      query: BRANDS_DIRECTORY_QUERY,
      notifyOnNetworkStatusChange: true,
      variables: {
        ...queryVariables(),
        after: endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const { endCursor: previousResultEndCursor, hasNextPage } = oc(
          previousResult
        ).BrandDirectory.pageInfo();
        if (!hasNextPage || !fetchMoreResult) {
          return previousResult;
        }

        const newBrands = oc(fetchMoreResult).BrandDirectory.edges();
        const pageInfo = oc(fetchMoreResult).BrandDirectory.pageInfo();
        const { endCursor: newResultEndCursor } = pageInfo;

        if (newResultEndCursor === previousResultEndCursor) return previousResult;

        return {
          BrandDirectory: {
            cursor: newResultEndCursor,
            edges: [...previousResult.BrandDirectory.edges, ...newBrands],
            __typename: previousResult.BrandDirectory.__typename,
            pageInfo,
          },
        };
      },
    });
  };
  const hasNextPage = oc(data).BrandDirectory.pageInfo.hasNextPage();
  return (
    <BrandListContent
      activeFilter={activeFilter}
      brands={reduceBrands(data)}
      hasNextPage={hasNextPage}
      loadingMore={networkStatus === 3}
      onFetchMore={onFetchMore}
    />
  );
};

// == Styles ===============================================================
